// Text Patterns / Creative / Hooks / Sequences: what the sample's listings
// share, counted per snapshot, each row expandable into the real examples.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { analyzeRun, getAssetAnalysis, getRunPatterns, type RunPatterns, type TextPattern, type VisualPattern } from '@/api/aso';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { Select } from '@/components/Select';
import { useCanEdit } from '@/components/EditOnly';
import { useToastStore } from '@/store/toast';
import { AssetImage } from './AssetImage';

export type PatternTab = 'text' | 'creative' | 'hooks' | 'sequences';

const fmt = (sec: number | null | undefined) => (sec ? new Date(sec * 1000).toLocaleString() : '—');
const pct = (n: number, total: number) => (total ? `${Math.round((n / total) * 100)}%` : '—');

function ShareBar({ n, total }: { n: number; total: number }) {
  const w = total ? Math.round((n / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 min-w-[160px]">
      <div className="h-1.5 flex-1 rounded bg-surface-muted overflow-hidden"><div className="h-full bg-primary" style={{ width: `${w}%` }} /></div>
      <span className="text-xs text-content-muted whitespace-nowrap">{n}/{total} · {pct(n, total)}</span>
    </div>
  );
}

export function usePatterns(runId: number) {
  return useQuery({
    queryKey: ['aso-patterns', runId],
    queryFn: () => getRunPatterns(runId),
    enabled: runId > 0,
    refetchInterval: (q) => (q.state.data?.run.analysis === 'queued' ? 10_000 : false),
  });
}

/** Status line + Analyze button, shared by the four pattern tabs. */
export function AnalysisBar({ runId, data }: { runId: number; data: RunPatterns | undefined }) {
  const canEdit = useCanEdit();
  const qc = useQueryClient();
  const toast = useToastStore((s) => s.push);
  const run = data?.run;
  const analyze = useMutation({
    mutationFn: (force: boolean) => analyzeRun(runId, force),
    onSuccess: (r) => { toast(`Analysis queued: ${r.vision} picture(s)${r.skipped ? `, ${r.skipped} skipped` : ''}`); qc.invalidateQueries({ queryKey: ['aso-patterns', runId] }); },
    onError: (e: any) => toast(e?.message ?? 'Failed to queue the analysis'),
  });
  if (!run) return null;
  const pendingPics = run.pending.analyze_asset ?? 0;
  const pendingText = run.pending.analyze_text ?? 0;
  const collecting = run.status === 'queued' || run.status === 'running';
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs text-content-muted">
      {collecting && <span>Still collecting — analysis starts when the snapshot settles.</span>}
      {!collecting && run.analysis === 'none' && <span>Not analysed yet.</span>}
      {run.analysis === 'queued' && (
        <span className="text-primary">Analysing… {pendingPics > 0 ? `${pendingPics} picture(s) left` : ''}{pendingText > 0 ? ' · text pending' : ''}</span>
      )}
      {run.analysis === 'done' && <span>Analysed {fmt(run.analyzed_at)}</span>}
      {run.analysis === 'failed' && <span className="text-danger">Analysis failed</span>}
      {run.analysis_error && <span className="text-warning" title={run.analysis_error}>{run.analysis_error}</span>}
      {canEdit && !collecting && run.analysis !== 'queued' && (
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="secondary" disabled={analyze.isPending} onClick={() => analyze.mutate(false)}>
            {run.analysis === 'done' ? 'Re-count' : 'Analyse'}
          </Button>
          {run.analysis === 'done' && (
            <button className="text-xs text-content-muted hover:underline" title="Send every picture to the vision model again (costs API calls)"
              onClick={() => { if (confirm('Re-run vision on every picture of this snapshot? This costs API calls.')) analyze.mutate(true); }}>
              Re-analyse pictures
            </button>
          )}
        </span>
      )}
    </div>
  );
}

// --- Text patterns ---------------------------------------------------------

const PLACEMENTS = [
  { value: 'title', label: 'Title' }, { value: 'short', label: 'Short description' }, { value: 'full', label: 'Full description' },
];
const NGRAMS = [{ value: '', label: 'Words & phrases' }, { value: '1', label: 'Single words' }, { value: '2', label: 'Two-word phrases' }, { value: '3', label: 'Three-word phrases' }];

export function TextPatternsTab({ runId }: { runId: number }) {
  const q = usePatterns(runId);
  const [placement, setPlacement] = useState('title');
  const [ngram, setNgram] = useState('');
  const [cluster, setCluster] = useState('');
  const [open, setOpen] = useState<TextPattern | null>(null);
  const data = q.data;

  const clusters = useMemo(() => {
    const used = new Set((data?.text ?? []).map((t) => t.cluster_slug).filter((c): c is string => !!c));
    return [{ value: '', label: 'All clusters' }, ...[...used].sort().map((c) => ({ value: c, label: data?.labels.cluster[c] ?? c }))];
  }, [data]);

  const rows = useMemo(() => (data?.text ?? []).filter((t) =>
    t.placement === placement && (!ngram || String(t.ngram) === ngram) && (!cluster || t.cluster_slug === cluster)), [data, placement, ngram, cluster]);

  const clusterSummary = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of (data?.text ?? []).filter((t) => t.placement === placement)) {
      const key = t.cluster_slug ?? '';
      m.set(key, Math.max(m.get(key) ?? 0, t.apps_count));
    }
    return [...m.entries()].filter(([k]) => k).sort((a, b) => b[1] - a[1]).slice(0, 12);
  }, [data, placement]);

  return (
    <div className="space-y-3">
      <AnalysisBar runId={runId} data={data} />
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={placement} options={PLACEMENTS} onChange={setPlacement} className="sm:w-52" />
        <Select value={ngram} options={NGRAMS} onChange={setNgram} className="sm:w-52" />
        <Select value={cluster} options={clusters} onChange={setCluster} className="sm:w-60" />
      </div>
      {clusterSummary.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {clusterSummary.map(([slug, n]) => (
            <button key={slug} onClick={() => setCluster(cluster === slug ? '' : slug)}
              className={`px-2 py-0.5 rounded-full text-xs border ${cluster === slug ? 'bg-primary text-white border-primary' : 'border-border text-content-muted hover:bg-surface-muted'}`}>
              {data?.labels.cluster[slug] ?? slug} · {n}
            </button>
          ))}
        </div>
      )}
      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">Phrase</th>
              <th className="text-left p-3 font-medium">Cluster</th>
              <th className="text-left p-3 font-medium">Apps using it</th>
              <th className="text-left p-3 font-medium">Examples</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-content-subtle">{data?.run.analysis === 'done' ? 'Nothing shared here.' : 'No text patterns yet.'}</td></tr>}
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-border hover:bg-surface-muted/40 cursor-pointer" onClick={() => setOpen(t)}>
                <td className="p-3 align-middle text-content font-medium">{t.phrase}</td>
                <td className="p-3 align-middle text-content-muted text-xs">{t.cluster_slug ? (data?.labels.cluster[t.cluster_slug] ?? t.cluster_slug) : '—'}</td>
                <td className="p-3 align-middle"><ShareBar n={t.apps_count} total={t.apps_total} /></td>
                <td className="p-3 align-middle text-xs text-content-subtle truncate max-w-[280px]">{t.examples.map((e) => e.name).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && (
        <Modal isOpen onClose={() => setOpen(null)} title={`“${open.phrase}” in ${open.placement}`} size="2xl">
          <div className="text-xs text-content-muted mb-3">{open.apps_count} of {open.apps_total} apps · {open.cluster_slug ? (data?.labels.cluster[open.cluster_slug] ?? open.cluster_slug) : 'no cluster'} · locale {open.locale}</div>
          <ul className="space-y-2">
            {open.examples.map((e, i) => (
              <li key={i} className="text-sm"><span className="font-medium text-content">{e.name}</span><div className="text-content-muted">{e.snippet}</div></li>
            ))}
          </ul>
        </Modal>
      )}
    </div>
  );
}

// --- Creative / Hooks ------------------------------------------------------

const ASSET_KINDS = [{ value: 'icon', label: 'Icons' }, { value: 'screenshot', label: 'Screenshots' }, { value: 'feature', label: 'Feature graphics' }];
const POSITIONS = [{ value: '', label: 'Any screenshot' }, ...Array.from({ length: 8 }, (_, i) => ({ value: String(i + 1), label: `Screen #${i + 1}` }))];

export function VisualPatternsTab({ runId, kind }: { runId: number; kind: 'tag' | 'hook' }) {
  const q = usePatterns(runId);
  const [assetKind, setAssetKind] = useState(kind === 'hook' ? 'screenshot' : 'icon');
  const [position, setPosition] = useState('');
  const [open, setOpen] = useState<VisualPattern | null>(null);
  const data = q.data;
  const rows = useMemo(() => (data?.visual ?? []).filter((v) =>
    v.kind === kind && v.asset_kind === assetKind && (assetKind !== 'screenshot' || (position ? v.position === Number(position) : v.position === null))),
  [data, kind, assetKind, position]);
  const labels = data?.labels[kind] ?? {};

  return (
    <div className="space-y-3">
      <AnalysisBar runId={runId} data={data} />
      <div className="flex flex-col sm:flex-row gap-2">
        <Select value={assetKind} options={ASSET_KINDS} onChange={setAssetKind} className="sm:w-52" />
        {assetKind === 'screenshot' && <Select value={position} options={POSITIONS} onChange={setPosition} className="sm:w-52" />}
      </div>
      <div className="bg-surface border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-surface-muted text-xs uppercase tracking-wide text-content-subtle">
            <tr>
              <th className="text-left p-3 font-medium">{kind === 'hook' ? 'Hook' : 'Pattern'}</th>
              <th className="text-left p-3 font-medium">Apps</th>
              <th className="text-left p-3 font-medium">Examples</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-content-subtle">{data?.run.analysis === 'done' ? 'Nothing here.' : 'No patterns yet.'}</td></tr>}
            {rows.map((v) => (
              <tr key={v.id} className="border-t border-border hover:bg-surface-muted/40 cursor-pointer" onClick={() => setOpen(v)}>
                <td className="p-3 align-middle text-content font-medium">{labels[v.slug] ?? v.slug}</td>
                <td className="p-3 align-middle"><ShareBar n={v.apps_count} total={v.apps_total} /></td>
                <td className="p-3 align-middle">
                  <div className="flex gap-1">
                    {v.examples.slice(0, 6).map((e) => (
                      <AssetImage key={e.hash} hash={e.hash} alt={labels[v.slug] ?? v.slug}
                        className={assetKind === 'icon' ? 'w-8 h-8 rounded-md object-cover' : 'h-12 w-auto rounded object-cover'} />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {open && <ExamplesModal title={`${labels[open.slug] ?? open.slug} · ${open.apps_count}/${open.apps_total} apps`} examples={open.examples} appNames={data?.app_names ?? {}} labels={data?.labels} onClose={() => setOpen(null)} />}
    </div>
  );
}

function ExamplesModal({ title, examples, appNames, labels, onClose }: {
  title: string; examples: { app_id: number; hash: string }[]; appNames: Record<number, string>;
  labels: RunPatterns['labels'] | undefined; onClose: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <Modal isOpen onClose={onClose} title={title} size="4xl">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {examples.map((e) => (
          <button key={e.hash} className="text-left" onClick={() => setPicked(e.hash)}>
            <AssetImage hash={e.hash} alt={appNames[e.app_id] ?? ''} className="w-full rounded-lg border border-border object-cover" />
            <div className="text-xs text-content-muted mt-1 truncate">{appNames[e.app_id] ?? `app ${e.app_id}`}</div>
          </button>
        ))}
      </div>
      {picked && <AssetAnalysisPanel hash={picked} labels={labels} />}
    </Modal>
  );
}

export function AssetAnalysisPanel({ hash, labels }: { hash: string; labels: RunPatterns['labels'] | undefined }) {
  const q = useQuery({ queryKey: ['aso-asset-analysis', hash], queryFn: () => getAssetAnalysis(hash), staleTime: 10 * 60 * 1000 });
  if (q.isLoading) return <div className="mt-3 text-xs text-content-subtle">Loading analysis…</div>;
  if (!q.data) return <div className="mt-3 text-xs text-content-subtle">Not analysed.</div>;
  const a = q.data;
  const raw = a.raw as Record<string, unknown>;
  const extras = Object.entries(raw).filter(([k]) => !['tags', 'primary_hook', 'secondary_hooks', 'summary', 'new_tags', 'new_hooks'].includes(k));
  return (
    <div className="mt-4 rounded-lg bg-surface-muted p-3 text-sm space-y-2">
      <div className="text-content">{a.summary}</div>
      <div className="text-xs"><span className="text-content-subtle">Hook:</span> <span className="text-content font-medium">{a.primary_hook ? (labels?.hook[a.primary_hook] ?? a.primary_hook) : '—'}</span>
        {a.secondary_hooks.length > 0 && <span className="text-content-muted"> + {a.secondary_hooks.map((h) => labels?.hook[h] ?? h).join(', ')}</span>}</div>
      <div className="flex flex-wrap gap-1">{a.tags.map((t) => <span key={t} className="px-1.5 py-0.5 rounded bg-surface text-[11px] text-content-muted border border-border">{labels?.tag[t] ?? t}</span>)}</div>
      {extras.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] text-content-muted">
          {extras.map(([k, v]) => <><dt key={`${k}-k`} className="text-content-subtle">{k}</dt><dd key={`${k}-v`}>{Array.isArray(v) ? v.join(', ') : String(v)}</dd></>)}
        </dl>
      )}
      <div className="text-[10px] text-content-subtle">{a.model} · {fmt(a.analyzed_at)}</div>
    </div>
  );
}

// --- Sequences --------------------------------------------------------------

export function SequencesTab({ runId }: { runId: number }) {
  const q = usePatterns(runId);
  const data = q.data;
  const hooks = data?.labels.hook ?? {};
  const [open, setOpen] = useState<number | null>(null);

  // Per-slot leaderboard from the visual hook rows with a position.
  const perSlot = useMemo(() => {
    const slots = new Map<number, { slug: string; n: number; total: number }[]>();
    for (const v of data?.visual ?? []) {
      if (v.kind !== 'hook' || v.asset_kind !== 'screenshot' || v.position === null) continue;
      const arr = slots.get(v.position) ?? [];
      arr.push({ slug: v.slug, n: v.apps_count, total: v.apps_total });
      slots.set(v.position, arr);
    }
    return [...slots.entries()].sort((a, b) => a[0] - b[0]).map(([pos, arr]) => [pos, arr.sort((a, b) => b.n - a.n).slice(0, 3)] as const);
  }, [data]);

  const seq = data?.sequences.find((s) => s.id === open);
  return (
    <div className="space-y-4">
      <AnalysisBar runId={runId} data={data} />
      {perSlot.length > 0 && (
        <div>
          <div className="label mb-2">Most common hook per screen</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
            {perSlot.map(([pos, arr]) => (
              <div key={pos} className="bg-surface border border-border rounded-lg p-2">
                <div className="text-[10px] uppercase tracking-wide text-content-subtle">Screen #{pos}</div>
                {arr.map((h) => (
                  <div key={h.slug} className="text-xs mt-1"><span className="text-content">{hooks[h.slug] ?? h.slug}</span> <span className="text-content-subtle">{h.n}/{h.total}</span></div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <div className="label mb-2">Most common sequences (first 5 screens)</div>
        <div className="bg-surface border border-border rounded-lg divide-y divide-border">
          {(data?.sequences ?? []).length === 0 && <div className="p-6 text-center text-content-subtle text-sm">No sequences yet.</div>}
          {(data?.sequences ?? []).slice(0, 30).map((s) => (
            <button key={s.id} className="w-full text-left p-3 hover:bg-surface-muted/40 flex flex-wrap items-center gap-2" onClick={() => setOpen(s.id)}>
              <span className="text-sm text-content">{s.sequence.map((h) => (h === 'none' ? '—' : hooks[h] ?? h)).join(' → ')}</span>
              <span className="ml-auto"><ShareBar n={s.apps_count} total={s.apps_total} /></span>
            </button>
          ))}
        </div>
      </div>
      {seq && (
        <Modal isOpen onClose={() => setOpen(null)} title={seq.sequence.map((h) => (h === 'none' ? '—' : hooks[h] ?? h)).join(' → ')} size="5xl">
          <div className="space-y-4">
            {seq.examples.map((e) => (
              <div key={e.app_id}>
                <div className="text-xs text-content-muted mb-1">{data?.app_names[e.app_id] ?? `app ${e.app_id}`}</div>
                <div className="flex gap-2 overflow-x-auto">
                  {e.hashes.map((h, i) => <AssetImage key={h} hash={h} alt={`screen ${i + 1}`} className="h-40 w-auto rounded-md border border-border" />)}
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
